import { Component, Inject, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_SNACK_BAR_DATA,
  MatSnackBarRef,
} from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

export interface CustomSnackbarData {
  // If translationKey is provided, it will be translated in the template.
  // Otherwise the raw message string will be shown.
  message?: string;
  translationKey?: string;
  translationParams?: Record<string, any>;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  duration?: number;
  onRetry?: () => void;
}

@Component({
  selector: 'app-custom-snackbar',
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslateModule],
  template: `
    <div class="custom-snackbar" [ngClass]="'snackbar-' + data.type">
      <div class="snackbar-content">
        <div class="snackbar-icon">
          <mat-icon class="!flex items-center justify-center">
            {{ getIconClass() }}
          </mat-icon>
        </div>
        <div class="snackbar-text">
          <!-- <div class="snackbar-title" *ngIf="data.title">{{ data.title }}</div> -->
          <div class="snackbar-message">
            <ng-container *ngIf="data.translationKey; else plainText">
              {{
                data.translationKey
                  | translate : (data.translationParams || {})
              }}
            </ng-container>
            <ng-template #plainText>{{ data.message }}</ng-template>
          </div>
        </div>
        <div class="snackbar-actions" *ngIf="data.onRetry">
          <button
            class="close-btn bg-transparent border border-white rounded-2xl py-2 px-6 flex"
            (click)="handleRetry()"
          >
            {{ 'COMMON.TRY_AGAIN' | translate }}
          </button>
        </div>
      </div>
      <div class="snackbar-progress" *ngIf="showProgress">
        <div class="progress-bar" [style.width.%]="progressWidth"></div>
      </div>
    </div>
  `,
  styleUrls: ['./custom-snackbar.component.scss'],
})
export class CustomSnackbarComponent implements OnDestroy {
  @Input() data: CustomSnackbarData;
  showProgress = true;
  progressWidth = 100;
  private progressInterval: any;

  constructor(
    @Inject(MAT_SNACK_BAR_DATA) public snackBarData: CustomSnackbarData,
    private snackBarRef: MatSnackBarRef<CustomSnackbarComponent>
  ) {
    this.data = snackBarData;
    this.startProgress();
  }

  getIconClass(): string {
    switch (this.data.type) {
      case 'success':
        return 'check_circle';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'info';
    }
  }

  close(): void {
    this.snackBarRef.dismiss();
  }

  handleRetry(): void {
    if (this.data.onRetry) {
      this.data.onRetry();
    }
    this.close();
  }

  private startProgress(): void {
    const duration = this.data.duration || 5000;
    const interval = 50; // Update every 50ms
    const steps = duration / interval;
    const decrement = 100 / steps;

    this.progressInterval = setInterval(() => {
      this.progressWidth -= decrement;
      if (this.progressWidth <= 0) {
        clearInterval(this.progressInterval);
        this.close();
      }
    }, interval);
  }

  ngOnDestroy(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
    }
  }
}
